import { useCallback, useMemo } from "react";
import type { FileExplorerFilters } from "../../types";
import { Button } from "@/shared/ui/button";
import { X } from "lucide-react";
import {
  clearAllFileFilters,
  deriveActiveFilterChips,
  removeFilterChip,
  type ActiveFilterChip,
} from "../../utils/fileUtils";
import { useTags } from "@/features/admin/queries";
import { DEFAULT_TAG_COLOR } from "@/services/api/tags";

interface FileExplorerActiveFiltersProps {
  filters: FileExplorerFilters;
  onFiltersChange: (filters: FileExplorerFilters) => void;
}

export function FileExplorerActiveFilters({
  filters,
  onFiltersChange,
}: FileExplorerActiveFiltersProps) {
  const chips = useMemo(() => deriveActiveFilterChips(filters), [filters]);
  const { data: availableTags = [] } = useTags();

  const tagColorMap = useMemo(() => {
    const map = new Map<string, string>();
    availableTags.forEach((tag) => {
      if (tag.name) {
        map.set(tag.name, tag.color || DEFAULT_TAG_COLOR);
      }
    });
    return map;
  }, [availableTags]);

  const handleRemoveChip = useCallback(
    (chip: ActiveFilterChip) => {
      const nextFilters = removeFilterChip(filters, chip);
      onFiltersChange(nextFilters);
    },
    [filters, onFiltersChange]
  );

  const handleClearAll = useCallback(() => {
    onFiltersChange(clearAllFileFilters());
  }, [onFiltersChange]);

  if (chips.length === 0) {
    return null;
  }

  return (
    <div className="border-b border-grid-200 bg-grid-50 px-4 py-2">
      <div className="flex flex-wrap items-center gap-2 text-xs text-ink-500">
        <span className="font-semibold uppercase tracking-wide text-ink-400">
          Active filters
        </span>

        {chips.map((chip) => {
          const isTag = chip.type === "tag";
          const tagColor = isTag
            ? tagColorMap.get(chip.value) ?? DEFAULT_TAG_COLOR
            : undefined;

          const chipStyle = isTag && tagColor
            ? {
                backgroundColor: `${tagColor}20`,
                borderColor: `${tagColor}40`,
              }
            : undefined;

          const textColor = isTag && tagColor && tagColor !== DEFAULT_TAG_COLOR
            ? tagColor
            : undefined;

          return (
            <button
              key={chip.key}
              type="button"
              className="flex items-center gap-1 rounded-full border border-grid-200 bg-white px-2 py-1 text-ink-700 shadow-sm transition hover:border-grid-300 hover:text-ink-900"
              style={chipStyle}
              onClick={() => handleRemoveChip(chip)}
              title={`Remove ${chip.label.toLowerCase()} ${chip.value}`}
            >
              {isTag && tagColor && (
                <span
                  className="inline-block h-2 w-2 rounded-full shrink-0"
                  style={{ backgroundColor: tagColor }}
                />
              )}
              <span className="font-medium" style={textColor ? { color: textColor } : undefined}>
                {chip.label}:
              </span>
              <span
                className="max-w-36 truncate text-ink-600"
                style={textColor ? { color: textColor } : undefined}
              >
                {chip.value}
              </span>
              <X className="h-3 w-3 shrink-0" />
            </button>
          );
        })}

        <Button
          variant="ghost"
          size="sm"
          className="ml-auto h-7 px-2 text-xs text-ink-500 hover:text-ink-900"
          onClick={handleClearAll}
        >
          Clear all
        </Button>
      </div>
    </div>
  );
}
